import metrikaCollector, {collectYandexReachGoal} from 'components/metrika';
import {getStoredThemePreference} from 'components/theme-toggle';

const YANDEX_COUNTER_ID = 46243086;
const VISIT_DATA_KEY = 'pt_theme_metrika_visit';
const YM_WAIT_MS = 500;
const YM_MAX_ATTEMPTS = 120;

function getAppliedTheme() {
    return document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
}

function getThemeSource() {
    const pref = getStoredThemePreference();

    if (pref === 'system') {
        return 'system';
    }

    if (pref === 'dark' || pref === 'light') {
        return 'manual';
    }

    return 'default';
}

function readVisitData() {
    try {
        const raw = sessionStorage.getItem(VISIT_DATA_KEY);
        if (!raw) {
            return null;
        }

        const data = JSON.parse(raw);
        if (data && (data.initial_theme === 'dark' || data.initial_theme === 'light')) {
            return data;
        }
    } catch (e) {}

    return null;
}

function writeVisitData(data) {
    try {
        sessionStorage.setItem(VISIT_DATA_KEY, JSON.stringify(data));
    } catch (e) {}
}

function ensureVisitData() {
    const existing = readVisitData();
    if (existing) {
        return existing;
    }

    const data = {
        initial_theme: getAppliedTheme(),
        theme_source: getThemeSource(),
        sent: false,
    };

    writeVisitData(data);

    return data;
}

function sendYandexParams(params) {
    if (typeof window.ym === 'function') {
        window.ym(YANDEX_COUNTER_ID, 'params', params);
        return true;
    }

    if (typeof yaCounter46243086 !== 'undefined' && typeof yaCounter46243086.params === 'function') {
        yaCounter46243086.params(params);
        return true;
    }

    return false;
}

function sendYandexReachGoal(goal, params) {
    if (typeof window.ym === 'function') {
        window.ym(YANDEX_COUNTER_ID, 'reachGoal', goal, params);
        return true;
    }

    if (typeof yaCounter46243086 !== 'undefined' && typeof yaCounter46243086.reachGoal === 'function') {
        yaCounter46243086.reachGoal(goal, params);
        return true;
    }

    return false;
}

function whenYmReady(callback, attempt = 0) {
    if (typeof window.ym === 'function' || typeof yaCounter46243086 !== 'undefined') {
        callback();
        return;
    }

    if (attempt >= YM_MAX_ATTEMPTS) {
        return;
    }

    setTimeout(() => whenYmReady(callback, attempt + 1), YM_WAIT_MS);
}

function sendVisitParamsOnce() {
    const visitData = ensureVisitData();

    if (visitData.sent) {
        return;
    }

    whenYmReady(() => {
        const current = readVisitData() || visitData;

        if (current.sent) {
            return;
        }

        try {
            if (!sendYandexParams({
                initial_theme: current.initial_theme,
                theme_source: current.theme_source,
            })) {
                return;
            }

            writeVisitData({...current, sent: true});
        } catch (e) {}
    });
}

function trackThemeSwitch(newTheme) {
    const theme = newTheme === 'dark' ? 'dark' : 'light';
    const params = {theme_changed_to: theme};

    whenYmReady(() => {
        try {
            if (!sendYandexReachGoal('theme_switch', params)) {
                return;
            }

            collectYandexReachGoal(metrikaCollector, 'theme_switch', {data: params});
        } catch (e) {}
    });
}

function bindThemeSwitchTracking() {
    document.addEventListener('pt-theme-change', (event) => {
        const detail = event.detail || {};

        if (!detail.userAction) {
            return;
        }

        const previousTheme = detail.previousTheme === 'dark' ? 'dark' : 'light';
        const nextTheme = detail.theme === 'dark' ? 'dark' : 'light';

        if (previousTheme === nextTheme) {
            return;
        }

        trackThemeSwitch(nextTheme);
    });
}

function initThemeMetrika() {
    ensureVisitData();
    sendVisitParamsOnce();
    bindThemeSwitchTracking();
}

export default initThemeMetrika;
